import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useState } from 'react'
import { IoCloseSharp } from 'react-icons/io5'
import { BsCheckCircleFill } from 'react-icons/bs'
import { OutlinePersonIcon } from 'components/Icons/OutlinePersonIcon'
import { ButtonAction } from 'pages/NotifyEvent/styles'
import { getCorIncidente, getDamage } from 'utils/damage'

export interface IFinishGroupTreatmentModalProps {
    isOpen: boolean
    handleCloseModal: () => void
    handleConfirmFinish: () => Promise<void>
    totalNotifications: number
    groupKey: string
    groupIncidentType?: string
    groupLocation?: string
    groupSector?: string
    groupDate?: string
    currentUserName?: string
    currentUserProfile?: string
    groupDamageRating?: string
}

export const FinishGroupTreatmentModal = ({
    isOpen,
    handleCloseModal,
    handleConfirmFinish,
    totalNotifications,
    groupKey,
    groupIncidentType = '',
    groupLocation = '',
    groupSector = '',
    groupDate = '',
    currentUserName = '',
    currentUserProfile = '',
    groupDamageRating = ''
}: IFinishGroupTreatmentModalProps) => {
    const [isLoading, setIsLoading] = useState(false)

    const handleConfirm = async () => {
        try {
            setIsLoading(true)
            await handleConfirmFinish()
        } catch (error) {
            console.error('Erro ao finalizar grupo:', error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div data-testid="test-finish-group-treatment-modal">
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-30"
                    onClose={() => { if (!isLoading) handleCloseModal() }}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-40" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-[692px] h-[437px] transform overflow-hidden rounded-[6px] bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    {/* Botão Fechar */}
                                    <button
                                        onClick={handleCloseModal}
                                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                                        disabled={isLoading}
                                    >
                                        <IoCloseSharp size={20} />
                                    </button>

                                    {/* Título */}
                                    <h2 className="text-lg font-semibold text-blueTheme">
                                        Finalizar tratamento em grupo
                                    </h2>
                                    <p className="text-gray-600 mt-1 text-sm">
                                        Ao finalizar um tratamento em grupo, não será possível editar as
                                        informações de seu tratamento.
                                    </p>

                                    {/* Card de tratamento */}
                                    <div className="border border-gray-300 rounded-lg p-4 mt-5">
                                        <div className="flex items-start gap-2">
                                            <span className='mt-0.5'>
                                                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M8.49097 16C7.4292 16 6.44147 15.8089 5.5278 15.4267C4.61413 15.0445 3.82194 14.5177 3.15125 13.8462C2.48056 13.1747 1.95434 12.3815 1.57261 11.4667C1.19087 10.552 1 9.56305 1 8.49999C1 7.44901 1.19006 6.46762 1.57019 5.55581C1.95032 4.64402 2.47653 3.85088 3.14883 3.17641C3.82114 2.50193 4.61413 1.97057 5.5278 1.58234C6.44147 1.19411 7.42749 1 8.48586 1C9.38966 1 10.2332 1.13976 11.0165 1.41927C11.7998 1.6988 12.5116 2.08834 13.1518 2.58789L12.0987 3.63206C11.6025 3.25752 11.0509 2.96586 10.4439 2.75707C9.83689 2.54829 9.18421 2.4439 8.48586 2.4439C6.76398 2.4439 5.32453 3.02126 4.1675 4.17598C3.01047 5.33069 2.43195 6.77314 2.43195 8.50332C2.43195 10.2335 3.01029 11.6766 4.16696 12.8325C5.32362 13.9884 6.76263 14.5663 8.48398 14.5663C10.2053 14.5663 11.645 13.9888 12.8029 12.8337C13.9608 11.6787 14.5398 10.2358 14.5398 8.50511C14.5398 8.17502 14.5143 7.85397 14.4635 7.54196C14.4127 7.22995 14.3399 6.92808 14.2451 6.63633L15.3729 5.49692C15.5725 5.9638 15.7239 6.4466 15.8271 6.94533C15.9303 7.44406 15.9819 7.96136 15.9819 8.49724C15.9819 9.56213 15.788 10.552 15.4003 11.4667C15.0125 12.3815 14.4818 13.1755 13.8081 13.8486C13.1345 14.5217 12.3423 15.0485 11.4316 15.4291C10.5209 15.8097 9.54068 16 8.49097 16ZM7.39894 11.9524L4.304 8.8356L5.32177 7.80637L7.39894 9.88605L14.9539 2.32192L16 3.35115L7.39894 11.9524Z" fill="#5FD290" />
                                                </svg>
                                            </span>
                                            <div className="text-left">
                                                <p className="text-gray-800 font-medium text-sm">
                                                    {groupIncidentType || 'Tipo do incidente do grupo'}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-base font-bold">
                                                        {groupKey}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {groupDate}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500 mb-2 mt-1">
                                                    {groupLocation} / {groupSector}
                                                </p>
                                                <span
                                                    className={`mt-1 chip-incidente incidente-${getCorIncidente(
                                                        getDamage(
                                                            groupDamageRating
                                                        )?.value
                                                    )}`}
                                                >
                                                    {`${getDamage(
                                                        groupDamageRating
                                                    )?.label
                                                        }`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Informações do finalizador */}
                                    <div className="mt-5 flex items-center justify-center flex-col text-center">
                                        <p className="text-xs font-normal text-darkGray80 text-center mb-2" style={{ color: '#474747CC' }}>
                                            Esse tratamento em grupo será finalizado por
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <OutlinePersonIcon width="20" height="20" color="#893367" />
                                            <span className="text-roxo font-medium">{currentUserName}</span>
                                        </div>
                                        <p className="text-sm text-gray-500">{currentUserProfile}</p>
                                    </div>

                                    {/* Botões */}
                                    <div className="flex justify-center gap-3 mt-6">
                                        <ButtonAction
                                            onClick={handleCloseModal}
                                            disabled={isLoading}
                                            className="border border-blueTheme text-blueTheme rounded-lg px-4 py-2"
                                        >
                                            Cancelar
                                        </ButtonAction>
                                        <ButtonAction
                                            onClick={handleConfirm}
                                            disabled={isLoading}
                                            className="bg-blueTheme hover:bg-blue-700 text-white rounded-lg px-4 py-2 transition disabled:opacity-50"
                                        >
                                            {isLoading ? 'Finalizando...' : 'Finalizar tratamento em grupo'}
                                        </ButtonAction>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    )
}
